import {
  DataService,
  TypedBaseListComponent,
  SharedModule,
  NotificationService,
  ModalService,
} from "@bb-vendure/admin-ui/core";

import { Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  CreatePackagesFromOrdersDocument,
  PackageReviewFilterParameter,
  PackageReviewsDocument,
  PerformSplitOrderDocument,
  UpdatePackageReviewDocument,
} from "src/generated-gql/graphql";
import { take, Observable } from "rxjs";
import { DataTable2Component } from "./custom-table/data-table2.component";
import ExpandingDivComponent from "./custom-table/components/expand_div/expand.component";
import PackageReviewOrderExpandContent from "./expand-content/content.component";
import { BulkUpdateSkuDialogComponent } from "../bulk-update-sku-dialog/bulk-update-sku-dialog.component";
import { BulkUpdateRuleDialogComponent } from "../bulk-update-rule-dialog/bulk-update-rule-dialog.component";
import { BulkAddSkuDialogComponent } from "../bulk-add-sku-dialog/bulk-add-sku-dialog.component";
import { BulkAddFreeGiftDialogComponent } from "../bulk-add-free-gift-dialog/bulk-add-free-gift-dialog.component";
import { IsCooperateColumnComponent } from "../is-cooperate/is-cooperate-column.component";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import { gql } from "@apollo/client";

const DELETE_PACKAGE_REVIEW = gql`
  mutation DeletePackageReview($id: ID!) {
    deletePackageReview(id: $id) {
      result
      message
    }
  }
`;

const SPLIT_ORDER_PACKAGE_REVIEWS = gql`
  mutation SplitOrderPackageReviews($ids: [ID!]!) {
    splitOrderPackageReviews(ids: $ids) {
      id
    }
  }
`;

@Component({
  templateUrl: "package-review-list.component.html",
  standalone: true,
  imports: [
    SharedModule,
    DataTable2Component,
    ExpandingDivComponent,
    PackageReviewOrderExpandContent,
    IsCooperateColumnComponent,
  ],
})
export class PackageReviewListComponent extends TypedBaseListComponent<
  typeof PackageReviewsDocument,
  "packageReviews"
> {
  activeRowId$: Observable<string | null>;
  expandedIds: string[] = [];
  expandedRowIndex: number | null = null;
  private multipleIds: string[] = [];

  constructor(
    protected dataService: DataService,
    private notificationService: NotificationService,
    protected modalService: ModalService,
    protected override route: ActivatedRoute
  ) {
    super();
    
    // Parse URL filters to get multiple IDs
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const filters = params['filters'];
      if (filters) {
        const idMatch = filters.match(/id:eq,([^&]*)/);
        if (idMatch && idMatch[1]) {
          const idValue = idMatch[1];
          if (idValue.includes(',')) {
            this.multipleIds = idValue.split(',').map((id) => id.trim()).filter((id) => id.length > 0);
          }
        }
      }
    });
    
    super.configure({
      document: PackageReviewsDocument,
      getItems: (data) => {
        return data.packageReviews;
      },
      setVariables: (skip, take) => ({
        options: {
          skip,
          take,
          filter: {
            sku: {
              contains: this.searchTermControl.value,
            },
            ...this.getCustomFilterInput(),
          },
          sort: this.sorts.createSortInput(),
        },
      }),
      refreshListOnChanges: [
        this.sorts.valueChanges,
        this.filters.valueChanges,
        this.searchTermControl.valueChanges,
      ],
    });
    
    // Clear any lingering selections on component initialization
    this.clearStaleSelection();
  }

  /**
   * Clear any stale selections that might persist from previous operations
   */
  private clearStaleSelection() {
    // Use setTimeout to ensure the component is fully initialized
    setTimeout(() => {
      if (this.selectionManager && this.selectionManager.selection.length > 0) {
        console.log('Clearing stale selection on component init');
        this.selectionManager.clearSelection();
      }
    }, 100);
  }

  /**
   * Get custom filter input, handling multiple IDs for the id filter
   */
  private getCustomFilterInput(): any {
    const baseFilter = this.filters.createFilterInput();

    // If we have multiple IDs from URL, use them with 'in' operator
    if (this.multipleIds.length > 0) {
      const { id, ...rest } = baseFilter;
      return {
        ...rest,
        id: { in: this.multipleIds },
      };
    }

    // Check if id filter has comma-separated values in text input
    if (baseFilter.id && typeof baseFilter.id === 'object' && 'eq' in baseFilter.id) {
      const idValue = baseFilter.id.eq as string;
      if (idValue && idValue.includes(',')) {
        const ids = idValue.split(',').map((id) => id.trim()).filter((id) => id.length > 0);
        if (ids.length > 0) {
          // Remove the eq filter and replace with in filter
          const { id, ...rest } = baseFilter;
          return {
            ...rest,
            id: { in: ids },
          };
        }
      }
    }

    return baseFilter;
  }

  readonly filters = this.createFilterCollection()
    .addFilter({
      name: "eventName",
      type: { kind: "text" },
      label: _("nav.event"),
      filterField: "eventName"
    })
    .addFilter({
      name: "productName",
      type: { kind: "text" },
      label: _("common.productName"),
      filterField: "productName"
    })
    .addFilter({
      name: "linkId",
      type: { kind: "text" },
      label: _("common.linkId"),
      filterField: "linkId"
    })
    .addFilter({
      name: "productId",
      type: { kind: "id" },
      label: _("productId"),
      filterField: "productId"
    })
    .addFilter({
      name: "eventId",
      type: { kind: "id" },
      label: _("eventId"),
      filterField: "eventId"
    })
    .addFilter({
      name: 'id',
      type: { kind: 'id' },
      label: _('common.id'),
      filterField: 'id',
    })
    .addDateFilters()
    .connectToRoute(this.route);

  readonly sorts = this.createSortCollection()
    .defaultSort("id", "DESC")
    .addSorts([
      { name: "id" },
      { name: "createdAt" },
      { name: "updatedAt" },
      { name: "totalQuantity" },
    ]);

  performSplitOrder() {
    this.dataService
      .mutate(PerformSplitOrderDocument)
      .pipe(take(1))
      .subscribe((res) =>
        this.notificationService.success("Updating split order...")
      );
  }

  createPackagesFromOrders() {
    // Get only the currently selected orders
    const currentSelection = this.selectionManager.selection;
    const ids = currentSelection.map((s) => s.orderId);
    
    this.dataService
      .mutate(CreatePackagesFromOrdersDocument, {
        orderIds: ids,
      })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.selectionManager.clearSelection();
          this.notificationService.success("Packages created successfully");
          this.refresh();
        },
        error: (error) => {
          this.notificationService.error(`Error creating packages: ${error.message}`);
          console.error('Error creating packages:', error);
        }
      });
  }

  setRowExpand(index: number) {
    if (this.expandedRowIndex === index) {
      this.expandedRowIndex = null;
    } else {
      this.expandedRowIndex = index;
    }
  }

  closeContents() {
    const params = { ...this.route.snapshot.params };
    delete params.contents;
    this.router.navigate(["./", params], {
      relativeTo: this.route,
      queryParamsHandling: "preserve",
    });
  }

  editSkuLine(e: any) {
    if (e) {
      this.notificationService.success("Update package review sucessfully");
      this.refresh();
    }
    // this.dataService
    //   .mutate(UpdatePackageReviewDocument, {
    //     input: {
    //       id: e.id,
    //       cloneOrderLines: e.cloneOrderLines,
    //     },
    //   })
    //   .pipe(take(1))
    //   .subscribe((res) => {
    //     if (res.updatePackageReview) {
    //       this.notificationService.success("Update package review sucessfully");
    //       this.refresh();
    //     }
    //   });
  }

  bulkUpdateSku() {
    if (this.selectionManager.selection.length <= 0) {
      return;
    }

    this.modalService
      .fromComponent(BulkUpdateSkuDialogComponent, {
        size: "lg",
        locals: {
          items: this.selectionManager.selection,
        },
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.refresh();
        }
      });
  }

  bulkAddSku() {
    if (this.selectionManager.selection.length <= 0) {
      return;
    }

    this.modalService
      .fromComponent(BulkAddSkuDialogComponent, {
        size: "lg",
        locals: {
          items: this.selectionManager.selection,
        },
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.refresh();
        }
      });
  }

  bulkAddFreeGift() {
    if (this.selectionManager.selection.length <= 0) {
      return;
    }

    this.modalService
      .fromComponent(BulkAddFreeGiftDialogComponent, {
        size: "lg",
        locals: {
          items: this.selectionManager.selection,
        },
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.refresh();
        }
      });
  }

  bulkUpdateRule() {
    if (this.selectionManager.selection.length <= 0) {
      return;
    }

    this.modalService
      .fromComponent(BulkUpdateRuleDialogComponent, {
        size: "lg",
        locals: {
          items: this.selectionManager.selection,
        },
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.refresh();
        }
      });
  }

  recalculatePackageReviews() {
    if (this.selectionManager.selection.length <= 0) {
      return;
    }

    this.dataService
      .mutate(SPLIT_ORDER_PACKAGE_REVIEWS, {
        ids: this.selectionManager.selection.map((s) => s.id),
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.notificationService.success("Package reviews recalculated successfully");
          this.refresh();
        }
      });
  }

  bulkDeletePackageReviews() {
    if (this.selectionManager.selection.length <= 0) {
      return;
    }

    const selectedCount = this.selectionManager.selection.length;
    
    if (!confirm(`Are you sure you want to delete ${selectedCount} package review(s)? This will also delete associated split orders.`)) {
      return;
    }

    const selectedIds = this.selectionManager.selection.map((s) => s.id);
    
    // Clear selection before making API calls
    this.selectionManager.clearSelection();
    
    // Delete each package review individually since there's no bulk delete API
    Promise.all(
      selectedIds.map(id => 
        this.dataService.mutate(DELETE_PACKAGE_REVIEW, { id }).pipe(take(1)).toPromise()
      )
    ).then((results: any[]) => {
      const successCount = results.filter(res => res?.deletePackageReview?.result === 'DELETED').length;
      const failCount = selectedIds.length - successCount;
      
      if (successCount > 0) {
        this.notificationService.success(`Successfully deleted ${successCount} package review(s) and associated data`);
      }
      
      if (failCount > 0) {
        this.notificationService.error(`Failed to delete ${failCount} package review(s)`);
      }
      
      this.refresh();
    }).catch((error) => {
      this.notificationService.error(`Error deleting package reviews: ${error.message}`);
      this.refresh();
      });
  }
}
